import * as React from 'react';
import {forwardRef, PropsWithChildren, ReactNode, useCallback, useEffect, useMemo, useRef} from 'react';
import {Glyph} from '../../types/Glyph';
import {isNil} from 'lodash-es';
import Helpers from '../../../shared-common/helpers/Helpers';
import {StackContainer} from '../../../shared-ui/components/StackContainer/StackContainer';
import {Icon} from '../Icon/Icon';
import {ButtonVariant} from '../../types/ButtonVariant';
import {ButtonSize} from '../../types/ButtonSize';
import {ButtonShape} from '../../types/ButtonShape';
import RenderHelpers from '../../../shared-ui/helpers/RenderHelpers';
import styles from './Button.module.css';
import {useSyncState} from '../../../shared-common/hooks/useSyncState';
import {SharedComponentProps} from '../../types/SharedComponentProps';
import {GlyphStyle} from '../../types/GlyphStyle';
import {Position} from '../../../shared-ui/types/Position';

export interface ButtonProps extends SharedComponentProps {
  icon?: Glyph | ReactNode;
  iconStyle?: GlyphStyle;
  iconPosition?: Position;
  text?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  isDisabled?: boolean;
  isLoading?: boolean;
  isSelected?: boolean;
  tabIndex?: number;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => Promise<void> | void;
  onMouseOver?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseOut?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseUp?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
}

type ButtonContent = 'children' | 'iconAndText' | 'icon' | 'text';

export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  (props, ref) => {
    const [getIsPressed, setIsPressed] = useSyncState(false);

    // A click often closes the dialog the button is in, unmounting it before its onClick finishes, so the button
    // must not update its state afterwards.
    const isMountedRef = useRef(true);
    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    const isLoading = props.isLoading === true;
    const isVisuallyDisabled = props.isDisabled === true;
    const isInteractionBlocked = isVisuallyDisabled || isLoading || getIsPressed();

    const variant: ButtonVariant = Helpers.ifNil(props.variant, 'secondary');
    const size: ButtonSize = Helpers.ifNil(props.size, 'default');
    const shape: ButtonShape = Helpers.ifNil(props.shape, 'circle');

    const iconPosition: Position = Helpers.ifNil(props.iconPosition, 'left');
    const isHorizontal = iconPosition === 'left' || iconPosition === 'right';

    let state: string = 'normal';
    if (isVisuallyDisabled) {
      state = 'disabled';
    } else if (getIsPressed()) {
      state = 'pressed';
    } else if (props.isSelected) {
      state = 'selected';
    }

    const handleClick = useCallback(
      async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (isInteractionBlocked) {
          return;
        }

        try {
          setIsPressed(true);

          if (isNil(props.onClick)) {
            return;
          }

          const result = props.onClick(event);
          if (result !== null && typeof result === 'object') {
            await result;
            return;
          }
        } finally {
          if (isMountedRef.current) {
            setIsPressed(false);
          }
        }
      },
      [props.onClick, getIsPressed, setIsPressed, isInteractionBlocked]
    );

    const contentType: ButtonContent = useMemo(
      () => {
        const hasChildren = React.Children.count(props.children) > 0;
        const hasText = !isNil(props.text);
        const hasIcon = !isNil(props.icon);

        if (hasChildren) {
          return 'children';
        }

        if (hasText && hasIcon) {
          return 'iconAndText';
        }

        if (hasIcon) {
          return 'icon';
        }

        return 'text';
      },
      [props.icon, props.text, props.children]
    );

    const gapWidth = size === 'compact' ? 4 : 8;

    const content = useMemo(
      () => {
        const iconNode = isLoading
          ? <Icon glyph="busy" glyphStyle={props.iconStyle} size={16}/>
          : typeof props.icon === 'string'
            ? <Icon glyph={props.icon} glyphStyle={props.iconStyle} size={16}/>
            : props.icon;

        switch (contentType) {
          case 'children':
            return props.children;
          case 'iconAndText':
            return (
              <StackContainer
                stackFrom={iconPosition}
                gapWidth={gapWidth}
                horizontalAlignment={isHorizontal ? undefined : 'center'}
                verticalAlignment={isHorizontal ? 'center' : undefined}
              >
                {iconNode}
                <span className={styles.text}>{props.text}</span>
              </StackContainer>
            );
          case 'icon':
            return isLoading
              ? <Icon glyph="busy" glyphStyle={props.iconStyle} size={size === 'compact' ? 16 : 24}/>
              : typeof props.icon === 'string'
                ? <Icon glyph={props.icon} glyphStyle={props.iconStyle} size={size === 'compact' ? 16 : 24}/>
                : props.icon;
          default:
          case 'text':
            return (<span className={styles.text}>{props.text}</span>);
        }
      },
      [contentType, isLoading, props.icon, props.text, props.children, gapWidth]
    );

    return (
      <button
        ref={ref}
        tabIndex={props.tabIndex}
        className={RenderHelpers.classes(
          styles.component,
          props.className,
          contentType === 'text' || contentType === 'iconAndText' ? styles.hasText : null,
          contentType === 'icon' || contentType === 'iconAndText' ? styles.hasIcon : null,
          contentType === 'icon' ? styles.noText : null
        )}
        style={props.style}
        data-variant={variant}
        data-size={size}
        data-shape={shape}
        data-state={state}
        data-loading={isLoading || undefined}
        aria-label={Helpers.ifBlank(props.ariaLabel, props.text)}
        aria-disabled={isInteractionBlocked}
        type="button"
        disabled={isVisuallyDisabled}
        onClick={handleClick}
        onMouseOver={props.onMouseOver}
        onMouseOut={props.onMouseOut}
        onMouseEnter={props.onMouseEnter}
        onMouseLeave={props.onMouseLeave}
        onMouseDown={props.onMouseDown}
        onMouseUp={props.onMouseUp}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
      >
        {content}
      </button>
    );
  }
);
